import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModelV1 } from '@/lib/models/policy-model-v1/policy-v1.model';
import { getOrgPolicyV1, patchPolicyV1 } from '@/lib/services/policy-v1.service';
import { FC, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import Drawer from '..';
import DrawerContent from '../content';
import DrawerHeader from '../header';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { UiOption } from '@/lib/helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import InputControl from '@/components/form/input-control';
import DrawerButtons from '@/components/form/drawer-buttons';
import { Form } from '@/components/form';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import useFeature from '@/hooks/use-feature.hook';
import { QueryKey } from '@/lib/query-client';
import StatusChip from '@/components/chip/status-chip';
import { getSdEntityConfig, SdEntityConfig } from '@/lib/models/policy-model-v1/sensitive-data';
import Text from '@/components/text';
import { Box, Stack } from '@mui/material';
import TextInput from '@/components/form/text-input';
import { cloneDeep } from 'lodash';
import Chip from '@/components/chip';
import { FieldError } from '@/lib/services';
import { DrawerProps } from '../helpers';

interface Props extends DrawerProps {
  id: string;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  // { label: 'Status', prop: 'enabled' },
  { label: 'Detection Mode', prop: 'scanType' },
];

type SdEntityFormModel = {
  enabled: boolean;
  scanType: string;
};

const getFormModel = (entityType: string, policy?: PolicyModelV1 | null): SdEntityFormModel => {
  const enabledEntity = policy?.sensitiveData.entities.find(({ type }) => type === entityType);
  return {
    enabled: !!enabledEntity,
    scanType: String(enabledEntity?.scanType || 1),
  };
};

const getReadonlyData = (data: SdEntityFormModel | null, entityConfig: SdEntityConfig | null) => {
  if (!data) {
    return {};
  }

  const { enabled, scanType } = data;
  const { name, scanTypes } = entityConfig || { name: 'UNKNOWN' };

  const { label, description } = scanTypes?.[Number(scanType)] || {
    label: scanType,
    description: '',
  };
  const hasDescription = !!description;

  return {
    name,
    enabled: <StatusChip value={enabled ? 'enabled' : 'disabled'} />,
    scanType: (
      <>
        {label}
        {hasDescription && (
          <Box mt={1}>
            <Text size="small">{description}</Text>
          </Box>
        )}
      </>
    ),
  };
};

export const SdEntityDrawer: FC<Props> = ({
  open,
  onClose,
  isTopDrawer,
  className,
  id: entityType,
  hideUpdateMenu,
}) => {
  const { toast, errorToast } = useToast();
  const { setMode, inWriteMode } = useViewMode('view');
  const { canChangePolicy } = useFeature();

  const query = useQuery([QueryKey.OrgPolicyView], async () => getOrgPolicyV1());
  const { data: policy, refetch } = query;

  const formHandle = useFormHandle(
    {
      initialValues: { name: '', enabled: false, scanType: '1' } as SdEntityFormModel,
      onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
        if (!policy) {
          return;
        }

        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) =>
            setFieldError(path.endsWith('scanType') ? 'scanType' : path, msg)
          );
        };

        const sensitiveData = cloneDeep(policy.sensitiveData);

        const scanType = Number(values.scanType || 1);

        const entityIndex = sensitiveData.entities.findIndex(({ type }) => type === entityType);

        if (entityIndex === -1) {
          sensitiveData.entities.push({
            type: entityType,
            scanType: scanType,
            enabled: false,
          });
        } else {
          sensitiveData.entities[entityIndex].scanType = scanType;
        }

        const { patched, error, fieldErrors } = await patchPolicyV1(policy.id, {
          sensitiveData,
        });

        setSubmitting(false);

        if (patched) {
          resetForm({ values });
          toast('The policy was updated');
          setMode('view');
          refetch();
          return;
        }

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        errorToast(error || 'The policy was not updated');
      },
    },
    { dirtyLockDisabled: true }
  );

  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  const handleCancel = () => {
    setMode('view');
    loadData(formModel);
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const showUpdateMenu = canChangePolicy;

  const [formModel, entityConfig, scanTypeOpts] = useMemo(() => {
    const formModel = getFormModel(entityType, policy);
    const entityConfig = getSdEntityConfig(entityType);
    const { scanTypes } = entityConfig || {};
    const scanTypeOpts: UiOption[] = scanTypes
      ? Object.keys(scanTypes).map((value) => {
          const { label, description, tooltip } = scanTypes[Number(value)];
          return {
            label: (
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Text>{label}</Text>
                {tooltip && <Chip label={tooltip} size="x-small" />}
              </Stack>
            ),
            description,
            value: String(value),
          };
        })
      : [];
    return [formModel, entityConfig, scanTypeOpts];
  }, [entityType, policy]);

  useEffect(() => {
    if (!policy) {
      return;
    }
    loadData(getFormModel(entityType, policy));
  }, [entityType, policy, loadData]);

  const hasScanTypes = !!entityConfig?.scanTypes;

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query}>
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          <MoreMenu menuId="sd-entity-menu">
            {showUpdateMenu && !hideUpdateMenu && (
              <UpdateMenuItems onEdit={handleEdit} canEdit={!inWriteMode} />
            )}
          </MoreMenu>
        }
      >
        {entityConfig?.name} Entity Settings
      </DrawerHeader>
      <DrawerContent>
        <Box mt={2}>
          {!inWriteMode ? (
            <ReadonlyView fields={fields} data={getReadonlyData(formModel, entityConfig)} />
          ) : (
            <Form formHandle={formHandle}>
              <TextInput
                name="name"
                label="Name"
                readonly
                value={entityConfig?.name || 'UNKNOWN'}
              />

              {hasScanTypes && (
                <InputControl
                  name="scanType"
                  label="Detection Mode"
                  type="select"
                  formHandle={formHandle}
                  options={scanTypeOpts}
                />
              )}

              <DrawerButtons
                submit
                cancel
                isNew={false}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                formHandle={formHandle}
              />
            </Form>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default SdEntityDrawer;
